let accessToken = null;
let companyId = null;
let refreshInProgress = false;
let refreshPromise = null;

// ✅ ZENTRALE API-URL
export const API_BASE_URL = process.env.REACT_APP_API_URL;

export function setAccessToken(token) {
  accessToken = token;
}

export function setCompanyId(id) {
  companyId = id;
}

/* =========================
   REFRESH TOKEN
========================= */
async function refreshToken() {
  if (refreshInProgress) {
    return refreshPromise;
  }

  refreshInProgress = true;

  refreshPromise = fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
    method: "POST",
    credentials: "include",
    headers: {
      "x-csrf": "1",
    },
  })
    .then(async (res) => {
      if (!res.ok) {
        const error = new Error("Refresh failed");
        error.status = res.status;
        error.code = "REFRESH_FAILED";
        throw error;
      }

      const data = await res.json();
      accessToken = data.accessToken;
      return accessToken;
    })
    .finally(() => {
      refreshInProgress = false;
    });

  return refreshPromise;
}

/* =========================
   API FETCH (with auto-refresh)
========================= */
export async function apiFetch(url, options = {}) {
  const { skipCompanyHeader, ...fetchOptions } = options;
  const headers = {
    "Content-Type": "application/json",
    ...(fetchOptions.headers || {}),
  };

  if (fetchOptions.method === "POST" || fetchOptions.method === "DELETE") {
    headers["x-csrf"] = "1";
  }

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  if (companyId && !skipCompanyHeader && headers["x-company-id"] === undefined) {
    headers["x-company-id"] = companyId;
  }

  let response = await fetch(`${API_BASE_URL}/api/v1${url}`, {
    ...fetchOptions,
    headers,
    credentials: "include",
  });

  // ✅ Erfolg
  if (response.ok) {
    return response.json();
  }

  // 🔐 401 → Nur refresh wenn wir einen Token hatten (geschützte Route)
  if (response.status === 401 && accessToken) {
    try {
      await refreshToken();

      headers.Authorization = `Bearer ${accessToken}`;

      response = await fetch(`${API_BASE_URL}/api/v1${url}`, {
        ...fetchOptions,
        headers,
        credentials: "include",
      });

      if (response.ok) {
        return response.json();
      }
    } catch (err) {
      const error = new Error("Session expired");
      error.status = 401;
      error.code = "SESSION_EXPIRED";
      throw error;
    }
  }

  // ❌ Fehler sauber weiterreichen
  let errorData = {};
  try {
    errorData = await response.json();
  } catch {
    // kein JSON
  }

  const error = new Error(errorData.message || "Request failed");
  error.status = response.status;
  error.code = errorData.code;
  error.response = { status: response.status };

  throw error;
}

/* =========================
   MULTIPART UPLOAD (FormData, mit Auto-Refresh)
========================= */
export async function apiUpload(url, formData, options = {}) {
  const { headers: extraHeaders, ...rest } = options;

  const buildHeaders = () => {
    // Kein Content-Type setzen: der Browser ergaenzt die multipart-Boundary.
    const headers = { "x-csrf": "1", ...(extraHeaders || {}) };
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    if (companyId && headers["x-company-id"] === undefined) {
      headers["x-company-id"] = companyId;
    }
    return headers;
  };

  const doFetch = () =>
    fetch(`${API_BASE_URL}/api/v1${url}`, {
      method: "POST",
      ...rest,
      headers: buildHeaders(),
      body: formData,
      credentials: "include",
    });

  let response = await doFetch();

  if (!response.ok && response.status === 401 && accessToken) {
    try {
      await refreshToken();
      response = await doFetch();
    } catch {
      const error = new Error("Session expired");
      error.status = 401;
      error.code = "SESSION_EXPIRED";
      throw error;
    }
  }

  if (response.ok) {
    return response.json();
  }

  let errorData = {};
  try {
    errorData = await response.json();
  } catch {
    // kein JSON
  }
  const error = new Error(errorData.message || "Upload failed");
  error.status = response.status;
  error.code = errorData.code;
  throw error;
}